import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, KeyRound, UserCog } from "lucide-react";
import PushNotifications from "@/components/PushNotifications";
import SystemTimePanel from "@/components/SystemTimePanel";

const inputCls =
  "w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent text-sm";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);

  const [un, setUn] = useState({ current_password: "", new_username: "" });
  const [unLoading, setUnLoading] = useState(false);

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("Konfirmasi password tidak sama");
      return;
    }
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password berhasil diubah");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPwLoading(false);
    }
  };

  const submitUn = async (e) => {
    e.preventDefault();
    setUnLoading(true);
    try {
      const { data } = await api.post("/auth/change-username", un);
      if (data.access_token) localStorage.setItem("aroma_token", data.access_token);
      toast.success("Username berhasil diubah");
      setUn({ current_password: "", new_username: "" });
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUnLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF]">Pengaturan</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">Akun &amp; Password</h1>
        <p className="text-sm text-[#4B5563] mt-2">
          Masuk sebagai <span className="font-mono-tab font-semibold">{user?.username}</span> · Peran{" "}
          <span className="font-mono-tab font-semibold uppercase">{user?.role}</span>
        </p>
      </div>

      <SystemTimePanel />

      <PushNotifications />

      <section className="bg-white border border-[#E5E7EB] rounded-md p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-md flex items-center justify-center bg-[#0055FF]/10 text-[#0055FF]">
            <UserCog className="h-4 w-4" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight">Ubah Username</h2>
            <p className="text-xs text-[#9CA3AF]">Ganti username login. Password lama diperlukan untuk verifikasi.</p>
          </div>
        </div>
        <form onSubmit={submitUn} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="form-change-username">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Username Baru</label>
            <input required minLength={3} value={un.new_username} onChange={(e) => setUn({ ...un, new_username: e.target.value })} className={inputCls} data-testid="input-new-username" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Password Saat Ini</label>
            <input type="password" required value={un.current_password} onChange={(e) => setUn({ ...un, current_password: e.target.value })} className={inputCls} data-testid="input-un-cur-pass" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" disabled={unLoading} data-testid="btn-change-username" className="h-10 px-5 rounded-md bg-[#0A0A0A] hover:bg-[#333] text-white font-semibold flex items-center gap-2 transition-colors">
              {unLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Simpan Username
            </button>
          </div>
        </form>
      </section>

      <section className="bg-white border border-[#E5E7EB] rounded-md p-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-md flex items-center justify-center bg-[#EA580C]/10 text-[#EA580C]">
            <KeyRound className="h-4 w-4" />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight">Ubah Password</h2>
            <p className="text-xs text-[#9CA3AF]">Gunakan password yang kuat, minimal 4 karakter.</p>
          </div>
        </div>
        <form onSubmit={submitPw} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="form-change-password">
          <div className="md:col-span-2">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Password Saat Ini</label>
            <input type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="input-cur-pass" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Password Baru</label>
            <input type="password" required minLength={4} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="input-new-pass" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-1.5">Konfirmasi Password Baru</label>
            <input type="password" required minLength={4} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} data-testid="input-confirm-pass" />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" disabled={pwLoading} data-testid="btn-change-password" className="h-10 px-5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold flex items-center gap-2 transition-colors">
              {pwLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Simpan Password
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
